import _ from "lodash";
import {CheckboxTreeMenu, SmallLoader} from "Ncontracts.Common.UI";
import PropTypes from "prop-types";
import React, {Component} from "react";
import {connect} from "react-redux";
import {withRouter} from "react-router-dom";
import {getContactsByGroupData} from "../../../actions/communications/newCommunicationAction";
import isEmpty from "../../../helpers/isEmpty";
import {ContactsByGroupModel} from "../../../Models/communications/ContactModel";

class SelectRecipientsByGroupForm extends Component {

    static propTypes = {
        contactData: PropTypes.shape(ContactsByGroupModel),
        fetching: PropTypes.bool,
        getContactData: PropTypes.func,
        match: PropTypes.object,
        onRecipientUpdate: PropTypes.func
    };

    constructor(props) {
        super(props);
        this.props.getContactData();
    }

    componentDidUpdate = () => {
        const eventId = this.props.match.params.eventId;
        if (this.props.fetching === false && !isEmpty(eventId)) {
            this.updateSelections([`event|${eventId}`], this.scrollToEvent);
        }
    };

    scrollToEvent = selectedIds => {
        let tries = 0;
        let intervalID = setInterval(() => {
            const eventElement = document.getElementById(`${selectedIds[0]}_li`);
            if (!isEmpty(eventElement)) eventElement.scrollIntoView();
            if (++tries === 10 || !isEmpty(eventElement)) {
                window.clearInterval(intervalID);
            }
        }, 250);
    };

    getTreeData = () => {
        if (this.props.fetching !== false) return [];

        let treeData = this.getEventData();

        if (!isEmpty(this.props.contactData.eventlessFunctions)) {
            treeData.unshift({
                id: 'allEventlessFunctions|0',
                label: '(Functions not assigned to Events)',
                expanded: false,
                children: this.mapFunctionData(this.props.contactData.eventlessFunctions)
            });
        }

        return treeData;
    };

    getEventData = () => {
        return isEmpty(this.props.contactData.events) ? [] : this.props.contactData.events.map(e => {
            const isSelected = this.props.match.params.eventId === e.id.toString();
            return {
                id: `event|${e.id}`,
                label: e.title,
                checked: isSelected,
                expanded: isSelected,
                children: this.mapFunctionData(e.functions, isSelected)
            };
        });
    };

    mapFunctionData = (functions, isExpanded) => {
        return functions.map(f => {
            const functionCallTreeData = isEmpty(f.callTrees) ? undefined : this.mapFunctionCallTreeData(f, isExpanded);
            const functionTeamData = isEmpty(f.teams) ? undefined : this.mapFunctionTeamData(f, isExpanded);
            let functionChildren = [];
            if (functionCallTreeData !== undefined) functionChildren.push(functionCallTreeData);
            if (functionTeamData !== undefined) functionChildren.push(functionTeamData);

            return {
                id: `function|${f.id}`,
                label: f.name,
                expanded: isExpanded,
                children: functionChildren
            };
        });
    };

    mapFunctionCallTreeData = (func, isExpanded) => {
        return {
            id: `functionCallTrees|${func.id}`,
            label: 'Call Trees',
            className: 'not-bold',
            expanded: isExpanded,
            children: func.callTrees.map(c => {
                return {
                    id: `callTree|${c.id}|${func.id}`,
                    label: c.name
                };
            })
        };
    };

    mapFunctionTeamData = (func, isExpanded) => {
        return {
            id: `functionTeams|${func.id}`,
            label: 'Teams',
            className: 'not-bold',
            expanded: isExpanded,
            children: func.teams.map(t => {
                return {
                    id: `team|${t.id}|${func.id}`,
                    label: t.name
                };
            })
        };
    };

    updateSelections = (selectedIds, callback) => {
        let updatedRecipients = [];

        for (const compoundId of selectedIds) {
            const [selectedItemType, selectedItemId] = _.split(compoundId, '|');

            switch (selectedItemType) {
                case 'allEventlessFunctions':
                    updatedRecipients.push(_.flatten(this.props.contactData.eventlessFunctions.map(f => this.getFunctionContacts(f))));
                    break;

                case 'event':
                    updatedRecipients.push(this.getContactsForEvent(selectedItemId));
                    break;

                case 'function':
                    updatedRecipients.push(this.getContactsForFunction(selectedItemId));
                    break;

                case 'functionCallTrees':
                case 'functionTeams':
                    updatedRecipients.push(this.getContactsForFunctionCallTreesOrTeams(selectedItemType, selectedItemId));
                    break;

                case 'callTree':
                case 'team':
                    updatedRecipients.push(this.getContactsForCallTreeOrTeam(selectedItemType, selectedItemId));
                    break;
            }
        }

        const sortedUniqueContacts = _.orderBy(_.uniqBy(_.flatten(updatedRecipients), 'id'), 'displayName');
        this.props.onRecipientUpdate(sortedUniqueContacts);
        if (!isEmpty(callback)) callback(selectedIds);
    };

    getContactsForEvent = selectedEventId => {
        const selectedEvent = _.find(this.props.contactData.events, ['id', _.parseInt(selectedEventId)]);
        if (isEmpty(selectedEvent)) return [];

        return _.flatten(selectedEvent.functions.map(f => this.getFunctionContacts(f)));
    };

    getFunctionContacts = func => {
        if (isEmpty(func)) return [];

        let functionContacts = isEmpty(func.contacts) ? [] : func.contacts.slice();

        if (!isEmpty(func.callTrees)) {
            functionContacts.push(_.flatten(func.callTrees.map(t => t.contacts)));
        }

        if (!isEmpty(func.teams)) {
            functionContacts.push(_.flatten(func.teams.map(t => t.contacts)));
        }

        return _.flatten(functionContacts);
    };

    getContactsForFunction = selectedFunctionId => {
        return this.getFunctionContacts(this.findSelectedFunction(selectedFunctionId));
    };

    getContactsForFunctionCallTreesOrTeams = (idType, selectedFunctionId) => {
        const selectedFunction = this.findSelectedFunction(selectedFunctionId);
        const groupType = idType === 'functionCallTrees' ? 'callTrees' : idType === 'functionTeams' ? 'teams' : idType;
        return isEmpty(selectedFunction) || isEmpty(selectedFunction[groupType]) ? [] : _.flatten(selectedFunction[groupType].map(t => t.contacts));
    };

    findSelectedFunction = selectedFunctionId => {
        const eventFunctions = _.flattenDeep(this.props.contactData.events.map(e => e.functions));
        let selectedFunction = _.find(eventFunctions, ['id', selectedFunctionId]);
        if (!selectedFunction) selectedFunction = _.find(this.props.contactData.eventlessFunctions, ['id', selectedFunctionId]);
        return selectedFunction;
    };

    getContactsForCallTreeOrTeam = (idType, selectedId) => {
        const groupType = idType === 'callTree' ? 'callTrees' : idType === 'team' ? 'teams' : idType;
        let allGroups = _.flattenDeep(this.props.contactData.events.map(e => e.functions.map(f => f[groupType])));
        allGroups = _.concat(allGroups, _.flatten(this.props.contactData.eventlessFunctions.map(f => f[groupType])));
        const selectedGroup = _.find(allGroups, ['id', _.parseInt(selectedId)]);
        return isEmpty(selectedGroup) || isEmpty(selectedGroup.contacts) ? [] : selectedGroup.contacts;
    };

    render() {
        if (this.props.fetching !== false) return <SmallLoader/>;

        return (
            <div className="recipient-group-container">
                <CheckboxTreeMenu
                    items={this.getTreeData()}
                    searchPlaceholder="Search"
                    updateSelectedItems={this.updateSelections}
                />
            </div>);
    }
}

const mapStateToProps = state => ({
    contactData: state.newCommunication.contactsByGroupData,
    fetching: state.newCommunication.fetching
});

const mapDispatchToProps = dispatch => ({
    getContactData: () => dispatch(getContactsByGroupData())
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SelectRecipientsByGroupForm));
